import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaTrash } from "react-icons/fa";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { deleteFile, getFilesByLead, uploadFile } from "../../store/slices/FileDataSlice";
import {
  fetchAllQuotations,
  downloadQuotationFile,
  deleteQuotation,
} from "../../store/slices/generateQuotationSlice";
import { isAdminAuthLevel } from "../../utils/opsLeadAttachments";

const FileUploadLeadReport = ({ LeadReport, setShowModal, isViewOnly = false }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const id = LeadReport?._id;
  const leadId = LeadReport?.id;
  const [SelectedCategory, setSelectedCategory] = useState("");

  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [fileNames, setFileNames] = useState([]);
  const [editfetchedfiles, seteditFetchedFiles] = useState({ payload: [] });
  const auth = localStorage.getItem("auth");
  const normalizedAuth = String(auth || "").trim().toUpperCase();
  const isAdmin = isAdminAuthLevel(auth);
  const isFinanceOrAccounting =
    normalizedAuth === "L2" || normalizedAuth === "L5";
  const isDirector = normalizedAuth === "L1";

  const { quotations = [] } = useSelector((state) => state.generateQuotation);

  const canShowAttachmentControls = !isAdmin && !isViewOnly;

  const fetchFiles = useCallback(async () => {
    if (isAdmin) return;
    if (!id) return;
    try {
      const files = await dispatch(getFilesByLead(id)).unwrap();
      seteditFetchedFiles(Array.isArray(files) ? { payload: files } : files);
    } catch (err) {
      console.error("Error fetching files:", err);
    }
  }, [dispatch, id, isAdmin]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (leadId != null && leadId !== undefined) {
      dispatch(fetchAllQuotations());
    }
  }, [dispatch, leadId]);

  const dropdownRefProbability = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefProbability.current &&
        !dropdownRefProbability.current.contains(event.target)
      ) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFileDownload = (fileData) => {
    if (isViewOnly) return;
    try {
      if (!fileData || !fileData.filepathurl) {
        throw new Error("Invalid file data");
      }
      const fileUrl = fileData.filepathurl;
      const fileName = fileData.filename || fileUrl.split("/").pop();
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", fileName);
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      enqueueSnackbar("File download started", { variant: "success" });
    } catch (error) {
      console.error("Download failed:", error);
      enqueueSnackbar("Failed to download file", { variant: "error" });
    }
  };

  const handleFileChange = (e) => {
    if (isViewOnly) return;
    const selectedFiles = Array.from(e.target.files);
    const allowedFormats = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!SelectedCategory) {
      enqueueSnackbar("Please select a category first", {
        variant: "error",
      });
      return;
    }

    setFileNames((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const validFiles = [];
      selectedFiles.forEach((file) => {
        if (!allowedFormats.includes(file.type)) {
          enqueueSnackbar(`Only PDF, DOC, DOCX, XLS, and XLSX files are allowed (${file.name})`, {
            variant: "error",
          });
          return;
        }
        const isDuplicate = [
          ...existing,
          ...(editfetchedfiles?.payload || [])
        ].some(
          (f) => f.name === file.name ||
            (f.filepath && f.filepath.split('/').pop() === file.name)
        );

        if (isDuplicate) {
          enqueueSnackbar(`File "${file.name}" is already uploaded`, {
            variant: "error",
          });
          return;
        }
        const fileWithCategory = new File([file], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
        fileWithCategory.selectedCategory = SelectedCategory;
        validFiles.push(fileWithCategory);
      });

      if (validFiles.length === 0) return prev;
      seteditFetchedFiles((prevFetched) => {
        const currentFiles = prevFetched?.payload || [];

        const newFiles = validFiles.map((file) => ({
          id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          filepath: `uploads/filesdata/${file.name}`,
          filepathurl: URL.createObjectURL(file),
          leadId: id,
          selectedCategory: file.selectedCategory,
          isNew: true,
          name: file.name,
          type: file.type
        }));
        handleFileUpload(id, newFiles);
        return {
          ...prevFetched,
          payload: [...currentFiles, ...newFiles]
        };
      });
      return [...existing, ...validFiles];
    });

    setSelectedCategory("");
    e.target.value = null;
  };

  const handleFileUpload = useCallback(
    async (id, newFiles) => {
      if (!newFiles || newFiles.length === 0) return;
      const uploadDate = new Date().toISOString().slice(0, 10);
      const formData = new FormData();
      formData.append("leadId", LeadReport._id);
      formData.append("uploadDate", uploadDate);
      try {
        for (const fileObj of newFiles) {
          const response = await fetch(fileObj.filepathurl);
          const blob = await response.blob();
          const file = new File([blob], fileObj.name, { type: fileObj.type });
          formData.append("selectedCategory", fileObj.selectedCategory);
          formData.append("files", file);
        }
        await dispatch(uploadFile(formData)).unwrap();
        dispatch(getFilesByLead(id));
        enqueueSnackbar("File uploaded successfully", { variant: "success" });
      } catch (error) {
        enqueueSnackbar("Error occurred while uploading file", {
          variant: "error",
        });
        console.error(
          "Error uploading file:",
          error.response?.data?.error || error.message
        );
      } finally {
        fetchFiles()
      }
    },
    [dispatch, enqueueSnackbar, fetchFiles, LeadReport._id]
  );

  const handleFileDelete = async (file) => {
    if (isViewOnly) return;
    const id = file.id;
    try {
      await dispatch(deleteFile(id)).unwrap();
      seteditFetchedFiles(prev => ({
        ...prev,
        payload: prev.payload.filter(f => f.id !== id)
      }));
      enqueueSnackbar("File deleted successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Error occurred while deleting file", { variant: "error" });
      console.error("Error deleting file:", error);
    } finally {
      fetchFiles()
    }
  };

  const quotationsForLead = (Array.isArray(quotations) ? quotations : []).filter(
    (q) => String(q.leadid) === String(leadId)
  );

  const handleQuotationDownload = async (quotation) => {
    if (isViewOnly) return;
    try {
      const { downloadUrl } = await dispatch(
        downloadQuotationFile(quotation._id)
      ).unwrap();
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `${quotation.quotationNumber || "quotation"}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (downloadUrl) window.URL.revokeObjectURL(downloadUrl);
      enqueueSnackbar("Quotation download started", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to download quotation", { variant: "error" });
      console.error("Quotation download failed:", error);
    }
  };

  const handleQuotationDelete = async (quotation) => {
    if (isViewOnly) return;
    try {
      await dispatch(deleteQuotation(quotation._id)).unwrap();
      enqueueSnackbar("Quotation deleted successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Error occurred while deleting quotation", { variant: "error" });
      console.error("Error deleting quotation:", error);
    }
  };

  const filesToDisplay = editfetchedfiles?.payload || [];
  const filesFilteredForUser = isFinanceOrAccounting
    ? filesToDisplay.filter((file) => {
        const cat = file.category || file.selectedCategory;
        return cat === "Purchase Order" || cat === "Contract";
      })
    : isDirector
      ? filesToDisplay.filter((file) => {
          const cat = file.category || file.selectedCategory;
          return cat === "Quote" || cat === "Purchase Order" || cat === "Contract";
        })
      : filesToDisplay;
  const combinedRows = isAdmin
    ? []
    : [
    ...filesFilteredForUser.map((file) => ({
      type: "file",
      key: `file-${file.id ?? file._id}`,
      srNo: 0,
      filename: file.filename || file.name,
      category: file.category || file.selectedCategory,
      file,
    })),
    ...(isFinanceOrAccounting
      ? []
      : quotationsForLead.map((q, idx) => ({
          type: "quotation",
          key: `quotation-${q._id}`,
          srNo: 0,
          filename: `quotation_v${idx + 1}`,
          category: "Quote",
          quotation: q,
        }))),
  ].map((row, index) => ({ ...row, srNo: index + 1 }));

  return (
    <div>
      <div className="fixed w-full inset-0 bg-black/50 flex items-center justify-center z-[999]">
        <div className="w-[50%] bg-white shadow-lg rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex align-middle items-center gap-2">
              <h2 className="text-lg font-bold">Attachments </h2>
              <p>({"Project Name : "}{LeadReport.ProjectName})</p>
            </div>
            <button
              className="text-gray-500 hover:text-black"
              onClick={() => setShowModal(false)}
            >
              Close
            </button>
          </div>
          {canShowAttachmentControls && (
          <div className="flex gap-2">
            <div className="relative bg-white">
              <div
                ref={dropdownRefProbability}
                className=" flex  p-0  rounded-xl align-middle text-[12px]"
              >
                <button
                  type="button"
                  disabled={isViewOnly}
                  onClick={() => {
                    if (isViewOnly) return;
                    setShowCategoryDropdown(!showCategoryDropdown);
                  }}
                  className={`border relative w-24 h-8 border-gray-400 text-nowrap rounded-md p-2 shadow-md overflow-hidden outline-none flex items-center justify-between bg-gray-200 ${isViewOnly ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <p className="text-left text-xs truncate">
                    {SelectedCategory ? SelectedCategory : "Category"}
                  </p>
                  <ExpandMoreIcon
                    color="white"
                    className="cursor-pointer bg-gray-200 flex-shrink-0"
                  />
                </button>

                {showCategoryDropdown && (
                  <div
                    style={{ zIndex: 9999 }}
                    className="absolute top-full left-0 w-40 p-1 cursor-pointer max-h-96 overflow-y-auto bg-white text-sm border border-gray-300 shadow-lg flex flex-col gap-1"
                  >
                    {isFinanceOrAccounting ? (
                      <>
                        <div
                          className="p-1 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            setSelectedCategory("Purchase Order");
                            setShowCategoryDropdown(!showCategoryDropdown);
                          }}
                        >
                          Purchase Order
                        </div>
                        <div
                          className="p-1 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            setSelectedCategory("Contract");
                            setShowCategoryDropdown(!showCategoryDropdown);
                          }}
                        >
                          Contract
                        </div>
                      </>
                    ) : isDirector ? (
                      <>
                        <div
                          className="p-1 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            setSelectedCategory("Quote");
                            setShowCategoryDropdown(!showCategoryDropdown);
                          }}
                        >
                          Quote
                        </div>
                        <div
                          className="p-1 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            setSelectedCategory("Purchase Order");
                            setShowCategoryDropdown(!showCategoryDropdown);
                          }}
                        >
                          Purchase Order
                        </div>
                        <div
                          className="p-1 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            setSelectedCategory("Contract");
                            setShowCategoryDropdown(!showCategoryDropdown);
                          }}
                        >
                          Contract
                        </div>
                      </>
                    ) : (
                      <>
                        {(auth === "L1" || auth === "L3" || auth === "L2") && (
                          <>
                            <div
                              className="p-1 hover:bg-gray-200 cursor-pointer"
                              onClick={() => {
                                setSelectedCategory("Quote");
                                setShowCategoryDropdown(!showCategoryDropdown);
                              }}
                            >
                              Quote
                            </div>
                            <div
                              className="p-1 hover:bg-gray-200 cursor-pointer"
                              onClick={() => {
                                setSelectedCategory("Purchase Order");
                                setShowCategoryDropdown(!showCategoryDropdown);
                              }}
                            >
                              Purchase Order
                            </div>
                          </>
                        )}

                        <div
                          className="p-1 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            setSelectedCategory("Contract");
                            setShowCategoryDropdown(!showCategoryDropdown);
                          }}
                        >
                          Contract
                        </div>
                        <div
                          className="p-1 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            setSelectedCategory("Communication");
                            setShowCategoryDropdown(!showCategoryDropdown);
                          }}
                        >
                          Communication
                        </div>
                        <div
                          className="p-1 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            setSelectedCategory("Miscellaneous");
                            setShowCategoryDropdown(!showCategoryDropdown);
                          }}
                        >
                          Miscellaneous
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <label
                className={`text-xs p-2 w-24 h-8 text-center rounded-md flex items-center justify-center ${isViewOnly
                  ? "bg-gray-300 text-gray-600 cursor-not-allowed opacity-50 pointer-events-none"
                  : !SelectedCategory
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-gray-800 text-white hover:text-black hover:bg-gray-300 cursor-pointer"
                  }`}
              >
                <span>Upload File</span>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileChange(e)}
                  disabled={isViewOnly || !SelectedCategory}
                />
              </label>

              <p className="truncate text-xs pl-1">
                {fileNames?.name || "No file selected"}
              </p>
              <div className="flex gap-2 text-red-500 text-[12px] pl-3">
                <p> only .pdf, .xlsx and .docx allowed</p>
              </div>
            </div>
          </div>
          )}

          <div className="w-full mt-4 text-left border-collapse text-[13px]">
            <div>
              <div className="border-b flex w-full">
                <div className="py-2 px-4 w-[15%]">Sr. No.</div>
                <div className="py-2 px-4 w-[40%]">File Name</div>
                <div className="py-2 px-4 text-center w-[15%]">Category</div>
                <div className="py-2 px-4 text-center w-[15%]">Download</div>
                <div className="py-2 px-4 text-center w-[15%]">Delete</div>
              </div>
            </div>
            <div>
              {combinedRows.length === 0 ? (
                <div className="py-4 px-4 text-center text-gray-500">
                  No attachments available.
                </div>
              ) : (
              combinedRows.map((row) => (
                <div className="border-b flex w-full" key={row.key}>
                  <div className="py-2 px-4 w-[15%]">{row.srNo}</div>
                  <div className="py-2 px-4 w-[40%] truncate overflow-hidden">{row.filename}</div>
                  <div className="py-2 px-4 w-[15%] text-center truncate">
                    {row.category}
                  </div>
                  <div className="py-2 px-4 text-center w-[15%]">
                    <button
                      onClick={() => {
                        if (isViewOnly) return;
                        row.type === "quotation"
                          ? handleQuotationDownload(row.quotation)
                          : handleFileDownload(row.file);
                      }}
                      disabled={isViewOnly}
                      className={`text-white px-2 py-1 rounded ${isViewOnly ? "bg-blue-500 opacity-50 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-700"}`}
                    >
                      <CloudDownloadIcon />
                    </button>
                  </div>
                  <div className="py-2 px-4 text-center w-[15%]">
                    <button
                      disabled={isViewOnly}
                      onClick={() => {
                        if (isViewOnly) return;
                        row.type === "quotation"
                          ? handleQuotationDelete(row.quotation)
                          : handleFileDelete(row.file);
                      }}
                      className={`text-white px-2 py-2 rounded ${isViewOnly ? "bg-red-500 opacity-50 cursor-not-allowed" : "bg-red-500 hover:bg-red-700"}`}
                    >
                      <FaTrash size={18} />
                    </button>
                  </div>
                </div>
              )))
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadLeadReport;